import { useQueryJurisdictions } from '@/hooks/query/useQueryJurisdictions';
import { Combobox, type ComboboxItem } from '@heidi/design-system/combobox';
import {
  compareSubdivisionsForCountry,
  getSubdivisionByCode,
  getSubdivisionsForCountry,
  jurisdictionSelectMessages,
  normalizeSubdivisionCode,
  useLocalizedSubdivisionName,
  type Subdivision,
} from '@heidi/growth';
import { useIso3166 } from '@heidi/utils/iso3166-lazy';
import { MapPin } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

interface LocalizedSubdivision extends Subdivision {
  localizedName: string;
}

interface JurisdictionSelectProps {
  /** ISO 3166-1 country name (e.g., "Australia", "United States") */
  countryName?: string | null;
  /** Currently selected jurisdiction code (e.g., "AU-NSW", "US-CA") */
  value?: string | null;
  /** Callback when jurisdiction is selected */
  onChange: (jurisdictionCode: string) => void;
  /** Optional className for the trigger button */
  className?: string;
  /** Label for the subdivision type (e.g. "State", "Province / Territory"). Used in placeholder text. */
  subdivisionLabel?: string;
  /** When true, the selector cannot be opened or changed. */
  disabled?: boolean;
  /**
   * When true, re-selecting the current jurisdiction unsets it, firing
   * `onChange('')`. Opt-in so required jurisdiction flows stay unchanged.
   */
  clearable?: boolean;
}

export const JurisdictionSelect = ({
  countryName,
  value,
  onChange,
  className,
  subdivisionLabel,
  disabled = false,
  clearable = false,
}: JurisdictionSelectProps) => {
  const intl = useIntl();
  const getLocalizedSubdivisionName = useLocalizedSubdivisionName();
  const { data: backendJurisdictionOptions = [] } = useQueryJurisdictions(countryName);
  // Lazy-load `iso-3166-2` on mount; non-null once the module resolves.
  // Included in dependency arrays below so memoised values recompute.
  const iso3166Module = useIso3166();

  const backendSubdivisions = useMemo<Subdivision[]>(
    () =>
      backendJurisdictionOptions
        .map((option) => {
          const normalizedCode = normalizeSubdivisionCode({
            countryName,
            code: option.code,
          });
          return {
            code: normalizedCode,
            // Backend is source of truth for canonical labels.
            name: option.name,
            type: option.type ?? 'State',
          };
        })
        .filter(
          (subdivision, index, allSubdivisions) =>
            allSubdivisions.findIndex((s) => s.code === subdivision.code) === index
        )
        .sort((a, b) => compareSubdivisionsForCountry(countryName, a, b)),
    [backendJurisdictionOptions, countryName]
  );

  const subdivisions = useMemo(
    () =>
      backendSubdivisions.length > 0
        ? backendSubdivisions
        : getSubdivisionsForCountry(countryName, iso3166Module),
    [backendSubdivisions, countryName, iso3166Module]
  );

  const localizedSubdivisions = useMemo<LocalizedSubdivision[]>(
    () =>
      subdivisions.map((subdivision) => ({
        ...subdivision,
        localizedName: getLocalizedSubdivisionName(subdivision),
      })),
    [subdivisions, getLocalizedSubdivisionName]
  );

  const selectedSubdivision = useMemo<LocalizedSubdivision | null>(() => {
    if (!value) return null;

    const selectedFromCountry = localizedSubdivisions.find(
      (subdivision) => subdivision.code === value
    );
    if (selectedFromCountry) return selectedFromCountry;

    // Alias / legacy codes may not match the list entry until normalized.
    // Only accept a fallback that resolves to a subdivision of the *current*
    // country — never show a jurisdiction from another country (EVI-2120).
    const fallbackSubdivision = getSubdivisionByCode(value, countryName, iso3166Module);
    if (!fallbackSubdivision) return null;

    const belongsToCountry = localizedSubdivisions.some(
      (subdivision) => subdivision.code === fallbackSubdivision.code
    );
    if (localizedSubdivisions.length > 0 && !belongsToCountry) {
      return null;
    }

    return {
      ...fallbackSubdivision,
      localizedName: getLocalizedSubdivisionName(fallbackSubdivision),
    };
  }, [
    value,
    localizedSubdivisions,
    countryName,
    getLocalizedSubdivisionName,
    iso3166Module,
  ]);

  const items = useMemo<ComboboxItem[]>(
    () =>
      localizedSubdivisions.map((subdivision) => ({
        value: subdivision.code,
        label: subdivision.localizedName,
        // Keep search matching the canonical name too, not just the localized label.
        keywords:
          subdivision.name && subdivision.name !== subdivision.localizedName
            ? [subdivision.name]
            : undefined,
      })),
    [localizedSubdivisions]
  );

  const hasCountry = Boolean(countryName);
  const hasSubdivisions = subdivisions.length > 0;

  const placeholder = !hasCountry
    ? intl.formatMessage(jurisdictionSelectMessages.selectCountryFirst)
    : !hasSubdivisions
      ? intl.formatMessage(jurisdictionSelectMessages.noRegionsAvailable)
      : subdivisionLabel
        ? intl.formatMessage(jurisdictionSelectMessages.selectSubdivision, {
            label: subdivisionLabel.toLowerCase(),
          })
        : intl.formatMessage(jurisdictionSelectMessages.selectStateRegion);

  const searchPlaceholder = subdivisionLabel
    ? intl.formatMessage(jurisdictionSelectMessages.searchSubdivision, {
        label: subdivisionLabel.toLowerCase(),
      })
    : intl.formatMessage(jurisdictionSelectMessages.searchStateRegion);

  const handleValueChange = useCallback(
    // `next` is null on the Combobox's clearable re-select path.
    (next: string | null) => onChange(next ?? ''),
    [onChange]
  );

  return (
    <Combobox
      className="w-full"
      data-testid="jurisdiction-select"
      buttonClassName={className}
      leadingIcon={<MapPin size={16} />}
      items={items}
      value={selectedSubdivision?.code ?? undefined}
      onValueChange={handleValueChange}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyText={intl.formatMessage(jurisdictionSelectMessages.noResultsFound)}
      searchBy="label"
      clearable={clearable}
      disabled={disabled || !hasCountry || !hasSubdivisions}
    />
  );
};
